import { App as AntdApp, message as staticMessage } from 'antd'
import { createContext, useContext, type ReactNode } from 'react'

/** antd v5 不再导出 MessageInstance 类型，从 App.useApp() 推导 */
type MessageInstance = ReturnType<typeof AntdApp.useApp>['message']

/**
 * 动态主题兼容的 message / notification / modal 实例 Provider
 *
 * 背景：antd v5 中直接 `import { message } from 'antd'` 调用静态方法时，
 * 无法消费 ConfigProvider 与动态主题 context，控制台会报警：
 *   `Static function can not consume context like dynamic theme. Please use 'App' component instead.`
 *
 * 方案：用 antd 的 `<App>` 组件包装应用，通过 `App.useApp()` 获取与 context 关联的实例，
 * 并用 React Context 分发给后代组件，避免每个组件都写 `App.useApp()`。
 *
 * 用法：
 *   // 顶层
 *   <MessageProvider>
 *     <App />
 *   </MessageProvider>
 *
 *   // 后代组件
 *   const message = useMessage()
 *   message.success('xxx')
 */

const MessageContext = createContext<MessageInstance>(staticMessage)

export function MessageProvider({ children }: { children: ReactNode }) {
  return (
    <AntdApp>
      <MessageBridge>{children}</MessageBridge>
    </AntdApp>
  )
}

/** 内部桥接组件：从 App.useApp() 取 message 实例并塞入 Context */
function MessageBridge({ children }: { children: ReactNode }) {
  const { message } = AntdApp.useApp()
  return <MessageContext.Provider value={message}>{children}</MessageContext.Provider>
}

/** 在任意后代组件中获取与 context 关联的 message 实例 */
export function useMessage(): MessageInstance {
  return useContext(MessageContext)
}
